import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Dropzone, { Accept, FileRejection } from "react-dropzone";
import { getLabelName } from "../../../utils";
import {ButtonBox} from "../button/button";

export interface EntityUploaderProps {
  acceptTypes: Accept;
  defaultImage?: string; // Optional default image URL
  handleFileReject?: (fileRejections: FileRejection[]) => void; // Optional prop
  handleFileDrop: (files: File[]) => void;
  handleDelete?: () => void;
  imgStyle?: React.CSSProperties;
  title?: string;
  showDeleteButton?: boolean; // Control visibility of delete button
  imageWidth?:  number|null; // Control image width
  imageHeight?: number|null; // Control image height
}

export const EntityUploader: React.FC<EntityUploaderProps> = React.memo(({
                                                                           acceptTypes,
                                                                           defaultImage,
                                                                           handleFileReject,
                                                                           title,
                                                                           imgStyle,
                                                                           handleFileDrop,
                                                                           handleDelete,
                                                                           showDeleteButton = false,
                                                                           imageWidth,
                                                                           imageHeight,
                                                                         }) => {
  //#region state
  const [isImageValid, setIsImageValid] = useState<boolean>(!!defaultImage);
  const prevImageUrlRef = useRef<string | undefined>(defaultImage);
  const imageValidationController = useRef<AbortController | null>(null);
  //#endregion
  //#region functions
  const defaultFileRejectHandler = useCallback((fileRejections: FileRejection[]) => {
    fileRejections.forEach((file) => {
      alert(
          `${file.file.name} ${file.errors[0].code}  ${file.errors[0].message}`
      );
    });
  }, []);
  // Handle file drop - memoized to prevent recreating on each render
  const onDropHandler = useCallback((files: File[]) => {
    handleFileDrop(files);
  }, [handleFileDrop]);
  // Delete button handler - memoized to prevent recreating on each render
  const onDeleteHandler = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleDelete) {
      handleDelete();
    }
  }, [handleDelete]);
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error("Failed to load image:", defaultImage);
    e.currentTarget.style.display = 'none';
    const parent = e.currentTarget.parentElement;
    if (parent) {
      parent.innerHTML = `<p style="font-size: 14px; line-height: normal; margin: 0;">${getLabelName("Image failed to load")}</p>`;
    }
    setIsImageValid(false);
  }, [defaultImage]);


  //#endregion
  //#region variables
  // Memoized styles to prevent recalculation on each render
  const containerStyle = useMemo<React.CSSProperties>(() => ({
    position: "relative",
    display: "inline-block",
    lineHeight: 0,
    fontSize: 0,
    boxSizing: "border-box"
  }), []);

  const dropzoneStyle = useMemo<React.CSSProperties>(() => ({
    width: imageWidth ?? "auto",
    height: imageHeight ?? "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
    border: "1px dashed #ccc",
    padding: 0,
    margin: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative"
  }), [imageWidth, imageHeight]);

  const imageContainerStyle = useMemo<React.CSSProperties>(() => ({
    width: imageWidth ?? "auto",
    height: imageHeight ?? "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: 0,
    margin: 0,
    lineHeight: 0,
    fontSize: 0,
    boxSizing: "border-box",
  }), [imageWidth, imageHeight]);

  const combinedImgStyle = useMemo<React.CSSProperties>(() => ({
    objectFit: "contain" as "contain",
    maxWidth: "100%",
    maxHeight: "100%",
    width: imageWidth ?? "100%",
    height: imageHeight ?? "auto",
    display: "block",
    margin: 0,
    padding: 0,
    ...(imgStyle || {})
  }), [imageWidth, imageHeight, imgStyle]);

  const placeholderStyle = useMemo<React.CSSProperties>(() => ({
    fontSize: "14px",
    lineHeight: "normal",
    margin: 0,
    padding: 0
  }), []);

  const deleteButtonContainerStyle = useMemo<React.CSSProperties>(() => ({
    marginTop: "8px"
  }), []);
  //#endregion
  //#region useEffect
  useEffect(() => {
    // Skip validation if the URL hasn't changed
    if (defaultImage === prevImageUrlRef.current) {
      return;
    }

    // Update the ref with current URL
    prevImageUrlRef.current = defaultImage;

    // Cancel any pending validations
    if (imageValidationController.current) {
      imageValidationController.current.abort();
      imageValidationController.current = null;
    }

    // Set initial state based on whether image exists
    if (!defaultImage) {
      setIsImageValid(false);
      return;
    }

    // Create a new controller for this validation
    const controller = new AbortController();
    imageValidationController.current = controller;
    const { signal } = controller;

    // Set a loading state if needed
    // setIsImageLoading(true);

    const img = new Image();

    // Setup event handlers
    img.onload = () => {
      // Only update state if not aborted
      if (!signal.aborted) {
        setIsImageValid(true);
        // setIsImageLoading(false);
      }
    };

    img.onerror = () => {
      // Only update state if not aborted
      if (!signal.aborted) {
        console.error("Failed to load image:", defaultImage);
        setIsImageValid(false);
        // setIsImageLoading(false);
      }
    };

    // Setup abort handler
    signal.addEventListener('abort', () => {
      // Cancel the image load by clearing its src
      img.src = '';
    });

    // Start loading the image
    img.src = defaultImage;

    // Cleanup function
    return () => {
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }
    };
  }, [defaultImage]);
 //#endregion
  //#region html
  return (
      <div className="entity-uploader-container" style={containerStyle}>
        <Dropzone
            key={`dropZone-LogoImage64`}
            accept={acceptTypes}
            maxFiles={1}
            onDrop={onDropHandler}
            onDropRejected={handleFileReject || defaultFileRejectHandler}
        >
          {({getRootProps, getInputProps}) => (
              <div style={{ lineHeight: 0, fontSize: 0 }}>
                <div
                    title={title}
                    {...getRootProps()}
                    className="file-upload-custom"
                    style={dropzoneStyle}
                >
                  <input title={title} {...getInputProps()} />
                  {defaultImage ? (
                      isImageValid ? (
                          <div className="image-container" style={imageContainerStyle}>
                            <img
                                src={defaultImage}
                                title={title}
                                alt="Default"
                                className="default-image"
                                style={combinedImgStyle}
                                onError={handleImageError}
                                loading="lazy" // Add lazy loading for better performance
                            />
                          </div>
                      ) : (
                          <p style={placeholderStyle}>{getLabelName("Invalid image or failed to load")}</p>
                      )
                  ) : (
                      <p style={placeholderStyle}>{getLabelName("Drag and drop here")}</p>
                  )}
                </div>
              </div>
          )}
        </Dropzone>

        {showDeleteButton && defaultImage && isImageValid && handleDelete && (
            <div style={deleteButtonContainerStyle}>
              <ButtonBox
                  iconType="delete"
                  id="deleteImageButton"
                  variant="danger"
                  className="btn-xs"
                  onClick={onDeleteHandler}
              >
                {getLabelName("Delete")}
              </ButtonBox>
            </div>
        )}
      </div>
  );
  //#endregion
});

// Add display name for debugging
EntityUploader.displayName = 'EntityUploader';